import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { FileText, Plus, Upload, UserRound, Layers, Wand2, ArrowRight, X, ArrowLeft, Search, Pencil, ChevronDown, ClipboardList } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { characterAvatar } from "../lib/characterAvatar";
import ProjectHeader from "../components/ProjectHeader";
import SegmentedControl from "../components/SegmentedControl";
import StateStream from "../components/StateStream";
import EditCharacterDialog from "../components/EditCharacterDialog";
import EmptyScriptOnboarding from "../components/EmptyScriptOnboarding";
import HMBackupBanner from "../components/HMBackupBanner";
import ConfirmDialog from "../components/ConfirmDialog";
import AllergyBadge from "../components/AllergyBadge";
import CharacterFittingsNotes from "../components/CharacterFittingsNotes";
import { useProjectAllergyMap } from "../lib/allergy";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import usePolling from "../hooks/usePolling";
// Iter-127 — shared episode grouping (same module as ScenesHub +
// OnSetMode) so Fittings is no longer capped to App 1.
import {
    NO_EPISODE,
    episodeLabelFor,
    groupScenesByEpisode,
    isTvProject,
} from "../lib/episodeGroups";
import { sortCharacters } from "../lib/characters";
import { formatSceneNumber } from "../lib/scenes";

const INT_EXT = ["INT", "EXT", "INT/EXT"];
const TOD = ["DAY", "NIGHT"];

export default function PrepMode() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const navigate = useNavigate();
    const location = useLocation();

    const [project, setProject] = useState(null);
    const [draftScenes, setDraftScenes] = useState([]);
    const [confirmedScenes, setConfirmedScenes] = useState([]);
    const [characters, setCharacters] = useState([]);
    const [script, setScript] = useState(null);
    const [tab, setTab] = useState("scenes");
    const [error, setError] = useState("");
    // Iter-158.13.2.20f.33 — H&M Cast allergy indicator source.
    // Feeds the "✚ Allergy" badge on Fittings Cast picker rows +
    // the selected-character header, mirroring the Character
    // Detail page's own header. Reuses the shared hook so the
    // "onsetflow:allergy-note-changed" event refreshes this
    // surface after an edit on Character Detail.
    const { byCharacterId: allergyByCharacterId } = useProjectAllergyMap(
        apiBase,
        projectId,
    );
    // Iter-157P.15 — structured warning state for zero-scene
    // Build/Rebuild failures. Backend returns a structured 422
    // payload (`script_yielded_zero_scenes`) with active_format +
    // filename + producer-friendly message; PrepMode renders this
    // in the same calm red block used in ScriptSection.jsx so the
    // user gets a clear explanation + recovery action instead of
    // a blank screen. Stored alongside — and distinct from —
    // `error` so we can surface either depending on backend reply.
    const [extractionWarning, setExtractionWarning] = useState(null);

    // scene add
    const [sceneNumber, setSceneNumber] = useState("");
    const [sceneHeading, setSceneHeading] = useState("");
    const [sceneIntExt, setSceneIntExt] = useState("");
    const [sceneTod, setSceneTod] = useState("");

    // character add
    const [charName, setCharName] = useState("");
    const [charActor, setCharActor] = useState("");
    const [selectedChar, setSelectedChar] = useState(null);
    // iter-67 PART 2: optional Pencil → opens EditCharacterDialog. We
    // keep the user pinned to the Fittings cast view: after save, we
    // refresh the character list AND patch `selectedChar` in place so
    // the user sees the updated values without losing their context.
    const [editCharacter, setEditCharacter] = useState(null);

    // script
    const [scriptText, setScriptText] = useState("");
    const [scriptTitle, setScriptTitle] = useState("");
    const [uploading, setUploading] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [savedMsg, setSavedMsg] = useState("");

    // Search — mirrors OnSetMode. Per-tab so the Scenes query and the
    // Characters query don't clobber each other when the user flips tabs.
    const [sceneQuery, setSceneQuery] = useState("");
    const [charQuery, setCharQuery] = useState("");
    // Reverse map of scene_id -> [character names], used to broaden the
    // scene haystack so typing a character name surfaces every scene that
    // character appears in (same behaviour as On Set search).
    const [sceneCharMap, setSceneCharMap] = useState({});

    const fileRef = useRef(null);
    const selectedCharRef = useRef(null);
    // Iter-157J.3 — StateStream exposes hasDraft() / discardDraft()
    // via this ref so the drawer can intercept implicit-destroy
    // actions (switching character, closing drawer) and ask the
    // user whether to discard or keep editing. Trust principle:
    // nothing disappears without an explicit user decision.
    const fittingStreamRef = useRef(null);
    const [discardConfirm, setDiscardConfirm] = useState(null);

    // iter-52 #4: write the working lens to localStorage so SceneDetail's
    // "Back to scenes" returns to Fittings (not silently flips to On Set)
    // when the user opened a scene from the Fittings surface.
    useEffect(() => {
        try { window.localStorage.setItem("onsetflow:scenes-lens", "prep"); } catch { /* ignore */ }
    }, []);

    // Iter-129 — Pilot reported the viewport jumping unexpectedly to
    // the bottom of the page when picking a character from the cast
    // list. Replace the smooth scrollIntoView with a gentle nearest-
    // edge nudge so the selected-character panel still becomes visible
    // when it's offscreen, but doesn't yank a user who was already
    // looking at the right place. `scrollIntoViewIfNeeded` is non-
    // standard but supported on Safari/iOS/Chrome — fall back to a
    // tighter scrollIntoView({block:"nearest"}) elsewhere.
    useEffect(() => {
        if (!selectedChar || !selectedCharRef.current) return;
        const el = selectedCharRef.current;
        if (typeof el.scrollIntoViewIfNeeded === "function") {
            el.scrollIntoViewIfNeeded(false);
        } else {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [selectedChar]);

    const refreshScenes = useCallback(async () => {
        const [{ data: draft }, { data: confirmed }] = await Promise.all([
            api.get(`${apiBase}/${projectId}/scenes`, { params: { status: "draft" } }),
            api.get(`${apiBase}/${projectId}/scenes`),
        ]);
        const idsEq = (a, b) =>
            a.length === b.length &&
            a.every((x, i) => x.id === b[i]?.id && x.updated_at === b[i]?.updated_at);
        setDraftScenes((prev) => (idsEq(prev, draft) ? prev : draft));
        setConfirmedScenes((prev) => (idsEq(prev, confirmed) ? prev : confirmed));
    }, [projectId]);

    useEffect(() => {
        (async () => {
            try {
                // Iter-158.13.2.20e.12 — Fittings freeze-blocker fix.
                // The project-level `GET .../:pid/script` endpoint is
                // retired on both beta mounts (returns HTTP 410 Gone
                // — see hm_workspace/scripts.py::_get_script_head,
                // guarded by `retire_project_level_scripts=True`).
                // Every consumer of the `script`/`scriptTitle`/
                // `scriptText` state on this page lives inside the
                // `tab === "script"` block, which is already replaced
                // by the "Open Script Setup" info card on beta at
                // L1058-1088. So on beta we skip the retired fetch
                // entirely and leave `script` at its initial null
                // state. Legacy `/projects/*` Fittings still calls
                // the endpoint (server.py L12441 remains active and
                // non-retired) so legacy behaviour is byte-identical.
                const isBeta = apiBase.startsWith("/beta/");
                const [{ data: p }, { data: ch }, { data: links }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`),
                    api.get(`${apiBase}/${projectId}/characters`),
                    api.get(`${apiBase}/${projectId}/scene-characters`),
                ]);
                setProject(p);
                setCharacters(ch);
                if (!isBeta) {
                    try {
                        const { data: sp } = await api.get(
                            `${apiBase}/${projectId}/script`,
                        );
                        setScript(sp);
                        setScriptTitle(sp?.title || "");
                        if (sp?.source === "paste") setScriptText(sp?.content || "");
                    } catch (scriptErr) {
                        // Non-fatal — Fittings still renders Scenes /
                        // Cast without a script payload. Surfacing a
                        // hard error here would recreate the freeze
                        // this iteration is repairing.
                        // eslint-disable-next-line no-console
                        console.warn(
                            "[PrepMode] optional legacy script fetch failed",
                            scriptErr,
                        );
                    }
                }
                // Build the same scene_id → [character names] map that
                // OnSetMode uses, so a scene-tab search for "Sergeant"
                // surfaces every scene featuring a Sergeant.
                const map = {};
                for (const l of links || []) {
                    if (!l?.scene_id || !l?.name) continue;
                    (map[l.scene_id] = map[l.scene_id] || []).push(l.name);
                }
                setSceneCharMap(map);
                await refreshScenes();
                // If we arrived from the Cast hub with a specific character
                // requested (router state.openCharacter), jump straight to
                // that character's panel so the user lands where they tapped.
                const wantId = location.state?.openCharacter;
                if (wantId) {
                    const target = (ch || []).find((c) => c.id === wantId);
                    if (target) {
                        setTab("characters");
                        setSelectedChar(target);
                    }
                }
            } catch (e) {
                setError(formatApiError(e));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // Iter-95 — keep Fittings (Prep) scene lists fresh without manual
    // refresh. Silent in-place merge preserves the user's tab + scroll.
    usePolling(refreshScenes, { intervalMs: 12000 });

    const addScene = async (e) => {
        e.preventDefault();
        if (!sceneHeading.trim()) return;
        if (duplicateSceneNumber) {
            setError("Scene number already exists.");
            return;
        }
        try {
            await api.post(`${apiBase}/${projectId}/scenes`, {
                heading: sceneHeading.trim(),
                scene_number: sceneNumber.trim() || null,
                int_ext: sceneIntExt || null,
                time_of_day: sceneTod || null,
            });
            setSceneHeading("");
            setSceneNumber("");
            setSceneIntExt("");
            setSceneTod("");
            setError("");
            await refreshScenes();
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const addCharacter = async (e) => {
        e.preventDefault();
        if (!charName.trim()) return;
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/characters`, {
                name: charName.trim(),
                actor: charActor.trim() || null,
            });
            setCharacters((c) => [...c, data]);
            setCharName("");
            setCharActor("");
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const saveScriptText = async (e) => {
        e.preventDefault();
        if (!scriptText.trim()) return;
        try {
            await api.post(`${apiBase}/${projectId}/script/text`, {
                content: scriptText,
                title: scriptTitle.trim() || null,
            });
            const { data: sp } = await api.get(`${apiBase}/${projectId}/script`);
            setScript(sp);
            setSavedMsg("Saved");
            setTimeout(() => setSavedMsg(""), 1500);
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const uploadScript = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`${apiBase}/${projectId}/script/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const { data: sp } = await api.get(`${apiBase}/${projectId}/script`);
            setScript(sp);
            setScriptTitle(sp?.title || file.name);
            setSavedMsg("Uploaded");
            setTimeout(() => setSavedMsg(""), 1500);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const parseNow = async () => {
        setParsing(true);
        setError("");
        setExtractionWarning(null);
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/script/parse`);
            if (!data.parsed_count) {
                setError(
                    "No scene headings detected. Ensure the script uses standard INT./EXT. formatting, or add scenes manually."
                );
                return;
            }
            navigate(`${routeBase}/${projectId}/scenes/review`);
        } catch (err) {
            // Iter-157P.15 — intercept the structured zero-scene
            // 422 payload and render the calm red warning block
            // instead of crashing the UI with a generic error
            // string. Pattern mirrors ScriptSection.jsx so the
            // producer sees identical recovery copy whether they
            // hit Build from Script Setup or from Fittings.
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (
                status === 422 &&
                detail &&
                typeof detail === "object" &&
                detail.error === "script_yielded_zero_scenes"
            ) {
                setExtractionWarning(detail);
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setParsing(false);
        }
    };

    const scriptReady = Boolean(script?.content);

    // Duplicate scene-number guard: compare case-insensitively against both
    // draft and confirmed scenes (covers active AND omitted — omitted scenes
    // are kept in the list with their number).
    const trimmedNewNumber = sceneNumber.trim();
    const duplicateSceneNumber =
        trimmedNewNumber.length > 0 &&
        [...draftScenes, ...confirmedScenes].some(
            (s) =>
                (s.scene_number || "").trim().toUpperCase() ===
                trimmedNewNumber.toUpperCase(),
        );

    // iter-50: Script removed from Fittings — script is a setup/admin
    // function (Settings → Script setup) and a read-only viewer (Script
    // tile on the project home), neither of which belong inside the
    // Fittings working surface. Tabs collapse to Scenes / Cast.
    const TABS = [
        { id: "scenes", label: "Scenes", icon: Layers, count: confirmedScenes.length },
        { id: "characters", label: "Cast", icon: UserRound, count: characters.length },
    ];

    // Search filters — same haystacks as OnSetMode so behaviour is
    // identical between the two modes. Empty query returns the full list.
    const sceneNeedle = sceneQuery.trim().toLowerCase();
    const filteredScenes = useMemo(() => {
        if (!sceneNeedle) return confirmedScenes;
        return confirmedScenes.filter((s) => {
            const chars = sceneCharMap[s.id] || [];
            const hay = [s.scene_number, s.heading, s.int_ext, s.time_of_day, ...chars]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase());
            return hay.some((v) => v.includes(sceneNeedle));
        });
    }, [sceneNeedle, confirmedScenes, sceneCharMap]);

    const charNeedle = charQuery.trim().toLowerCase();
    // Iter-127 — group filtered confirmed scenes by episode for the
    // Fittings view. Same SHARED helper as OnSetMode + ScenesHub so
    // ordering is byte-identical across surfaces. Episodic projects
    // get an accordion (always expanded by default — Fittings is an
    // action surface, not a browse surface); pure-film projects fall
    // through to the existing flat list.
    const groupedFilteredScenes = useMemo(
        () => groupScenesByEpisode(filteredScenes),
        [filteredScenes],
    );
    const isTvMode = useMemo(
        () => isTvProject(groupScenesByEpisode(confirmedScenes)),
        [confirmedScenes],
    );
    // Iter-129 — Fittings episode dropdowns default CLOSED. Pilot
    // requirement: long pages of expanded scenes felt noisy. We start
    // every episode collapsed (only on episodic projects — film
    // projects still render the flat list) and remember the user's
    // touches so re-renders don't fight them.
    const [collapsedEps, setCollapsedEps] = useState(() => new Set());
    const userTouchedCollapseRef = useRef(false);
    // Iter-155B.1 — restore the episode the user came from.
    const expandEpisodeFromNav = location.state?.expandEpisode || null;
    const scrollToSceneId = location.state?.scrollToSceneId || null;
    useEffect(() => {
        if (userTouchedCollapseRef.current) return;
        if (!isTvMode) return;
        const allKeys = groupedFilteredScenes
            .filter((g) => g.key !== NO_EPISODE)
            .map((g) => g.key);
        if (allKeys.length === 0) return;
        setCollapsedEps((prev) => {
            // Only fill if we haven't already auto-filled.
            if (prev.size > 0) return prev;
            // Iter-155B.1 — leave the nav-state episode expanded.
            return new Set(
                allKeys.filter((k) => k !== expandEpisodeFromNav),
            );
        });
        if (scrollToSceneId) {
            setTimeout(() => {
                const el = document.querySelector(
                    `[data-testid="scene-row-${scrollToSceneId}"]`,
                );
                if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ block: "center", behavior: "auto" });
                }
            }, 60);
        }
    }, [groupedFilteredScenes, isTvMode, expandEpisodeFromNav, scrollToSceneId]);
    const toggleEpisode = (key) => {
        userTouchedCollapseRef.current = true;
        setCollapsedEps((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Iter-127 — extracted scene-row render so the flat list AND the
    // grouped accordion produce identical markup.
    // Iter-155B.1 — accepts `episodeKey` so the Back-from-detail nav
    // can restore the right episode and scroll position.
    const renderPrepSceneRow = (s, episodeKey) => {
        if (s.omitted) {
            // Iter-157P.18.2 — list views answer ONE question:
            // "is this scene omitted?". Amber = omitted regardless
            // of cause. Detailed explanation lives on Scene Detail.
            const byRevision = Boolean(s.omitted_by_revision_id);
            return (
                <li key={s.id}>
                    <Link
                        data-testid={`scene-row-${s.id}`}
                        data-omitted="true"
                        data-omission-cause={byRevision ? "revision" : "script"}
                        to={`${routeBase}/${projectId}/scenes/${s.id}`}
                        state={{
                            returnTo: {
                                path: `${routeBase}/${projectId}/prep`,
                                label: "Back to Fittings",
                                expandEpisode: episodeKey || null,
                                scrollToSceneId: s.id,
                            },
                        }}
                        title={
                            byRevision
                                ? "Omitted by approved script revision. Continuity preserved. Tap the scene for full details."
                                : "Marked omitted in the original script. Continuity preserved. Tap the scene for full details."
                        }
                        className="tap min-h-[56px] flex items-center gap-3 rounded-lg border border-dashed border-amber-400/60 bg-amber-500/10 px-4 py-3 hover:bg-amber-500/15"
                    >
                        <div className="mono text-base w-10 text-center font-semibold text-amber-100">
                            {formatSceneNumber(s) || "—"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <span
                                data-testid={`scene-row-${s.id}-omitted-badge`}
                                className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full font-semibold bg-amber-500/30 text-amber-50 border border-amber-400/60"
                            >
                                Omitted
                            </span>
                        </div>
                    </Link>
                </li>
            );
        }
        return (
            <li key={s.id}>
                <Link
                    data-testid={`scene-row-${s.id}`}
                    to={`${routeBase}/${projectId}/scenes/${s.id}`}
                    state={{
                        returnTo: {
                            path: `${routeBase}/${projectId}/prep`,
                            label: "Back to Fittings",
                            expandEpisode: episodeKey || null,
                            scrollToSceneId: s.id,
                        },
                    }}
                    className="tap min-h-[64px] flex items-center gap-4 rounded-lg border border-white/10 bg-card px-4 py-3 hover:bg-white/5"
                >
                    <div className="mono text-base text-neutral-300 w-10 text-center font-semibold">
                        {formatSceneNumber(s) || "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-white text-base font-medium truncate">
                            {s.heading}
                        </div>
                        {(s.int_ext || s.time_of_day) ? (
                            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
                                {[s.int_ext, s.time_of_day].filter(Boolean).join(" · ")}
                            </div>
                        ) : null}
                    </div>
                    <ArrowRight size={18} className="text-neutral-500" />
                </Link>
            </li>
        );
    };
    const filteredCharacters = useMemo(() => {
        // Iter-129 — natural-numeric sort on character_number, then
        // filter. Sorting first means the search-narrowed list also
        // shows in the canonical order.
        const sorted = sortCharacters(characters);
        if (!charNeedle) return sorted;
        return sorted.filter((c) =>
            // Same haystack as OnSetMode + AssignmentDialog: name, actor,
            // and character_number all match. Lets users type "65" to
            // find "#65 Bob" or "Sergeant" to find any Sergeant.
            [c.name, c.actor, c.character_number]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(charNeedle)
        );
    }, [charNeedle, characters]);

    return (
        <div data-testid="prep-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle="Fittings" />
            {/* Iter-158.13.2.20f.2 #5 — H&M-only backup warning banner. */}
            <HMBackupBanner
                projectId={projectId}
                scriptPresent={
                    (Array.isArray(confirmedScenes) && confirmedScenes.length > 0)
                    || (Array.isArray(draftScenes) && draftScenes.length > 0)
                }
            />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                {location.state?.from === "cast" ? (
                    <button
                        type="button"
                        data-testid="prep-back-to-cast"
                        onClick={() =>
                            navigate(`${routeBase}/${projectId}/cast`, {
                                state: { lens: "prep", team: location.state?.team },
                            })
                        }
                        className="tap inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-4"
                    >
                        <ArrowLeft size={14} />
                        Back to Cast
                    </button>
                ) : null}
                <div className="mb-6 flex items-start gap-3">
                    <div
                        data-testid="prep-identity-icon"
                        className="h-10 w-10 rounded-lg bg-teal-500/15 border border-teal-400/30 text-teal-300 flex items-center justify-center shrink-0"
                    >
                        <ClipboardList size={18} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="font-display text-4xl font-black text-white">Fittings</h1>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-teal-200/70 mt-1">
                            Prep
                        </p>
                    </div>
                </div>

                {/* Iter-151 (P1) — legacy draft-scene approval banner
                    removed. Iter-149.1 made parsed scenes
                    immediately usable everywhere (Overview, On Set,
                    Fittings, Scenes hub) regardless of draft/confirmed
                    status, so routing the user to /scenes/review
                    from Fittings is no longer correct. Needs Review
                    on the Cast hub remains the only advisory surface.
                    The `draftScenes` state below stays so the
                    duplicate-scene-number guard keeps working across
                    both draft and confirmed scenes. */}

                <SegmentedControl
                    testid="prep-tabs"
                    ariaLabel="Fittings sections"
                    value={tab}
                    onChange={(nextTab) => {
                        // Iter-157J.3 — switching away from the Cast
                        // tab destroys the character drawer (and any
                        // pending fitting draft). Guard with the
                        // standard Discard / Continue editing dialog.
                        if (
                            tab === "characters" &&
                            nextTab !== "characters" &&
                            fittingStreamRef.current?.hasDraft?.()
                        ) {
                            setDiscardConfirm({
                                reason: "switch-tab",
                                apply: () => setTab(nextTab),
                            });
                            return;
                        }
                        setTab(nextTab);
                    }}
                    options={TABS.map((t) => ({
                        value: t.id,
                        label: t.label,
                        icon: t.icon,
                        testid: `prep-tab-${t.id}`,
                    }))}
                    className="mb-6"
                />

                {error ? (
                    <p data-testid="prep-error" className="text-sm text-red-400 mb-4">
                        {error}
                    </p>
                ) : null}

                {extractionWarning ? (() => {
                    // Iter-157P.19 — readability & persistence pass.
                    // Mirrors the ScriptSection block: explicitly
                    // calls out the most common cause (a "Print to
                    // PDF" / image-only export) and gives a
                    // concrete export setting to try. Light-mode-
                    // safe colour ramp (red-200 / red-300 instead
                    // of red-100). The block remains permanently
                    // rendered until a successful build clears it
                    // — no toast, no auto-dismiss.
                    const w = extractionWarning;
                    const active = w.active_format;
                    const headline = active === "DOCX"
                        ? "We couldn\u2019t identify any scenes in this DOCX."
                        : active === "PDF"
                            ? "We couldn\u2019t identify any scenes in this PDF."
                            : "We couldn\u2019t identify any scenes in this script.";
                    const explanation = active === "DOCX"
                        ? "This DOCX may have been exported in a format that doesn\u2019t preserve readable scene headings."
                        : "This PDF may have been exported in a format that doesn\u2019t preserve readable text.";
                    const recommendation = active === "DOCX"
                        ? "Try re-exporting the DOCX from your screenwriting tool, or upload the PDF version of the script."
                        : active === "PDF"
                            ? "Try exporting the PDF using \u201CBest for electronic distribution and accessibility\u201D, or upload the DOCX version of the script."
                            : "Try exporting the PDF using \u201CBest for electronic distribution and accessibility\u201D, or upload the DOCX version of the script.";
                    return (
                        <div
                            data-testid="prep-extraction-warning"
                            role="alert"
                            aria-live="polite"
                            className="mb-4 rounded-xl border border-red-500/25 bg-red-500/10 p-4 space-y-2"
                        >
                            <p className="text-sm text-red-300 font-semibold leading-snug">
                                {headline}
                            </p>
                            <p className="text-[13px] text-red-200 leading-relaxed">
                                {explanation}
                            </p>
                            <p className="text-[13px] text-red-200 leading-relaxed">
                                <span className="font-semibold">Try this:</span>{" "}
                                {recommendation}
                            </p>
                            <p className="text-[13px] text-emerald-300 leading-relaxed">
                                Your project is safe. No changes have been made.
                            </p>
                            <div className="pt-1">
                                <button
                                    type="button"
                                    data-testid="prep-extraction-warning-dismiss"
                                    onClick={() => setExtractionWarning(null)}
                                    className="tap inline-flex items-center h-7 px-3 rounded-full text-[11px] uppercase tracking-[0.2em] text-red-200 border border-red-500/30 hover:bg-red-500/15"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    );
                })() : null}

                {tab === "scenes" ? (
                    <section data-testid="scenes-section">
                        <form
                            onSubmit={addScene}
                            className="rounded-xl border border-white/10 bg-card p-4 mb-5 space-y-3"
                        >
                            <div className="grid grid-cols-3 gap-2">
                                <Input
                                    data-testid="scene-number-input"
                                    placeholder="#"
                                    value={sceneNumber}
                                    onChange={(e) => setSceneNumber(e.target.value)}
                                    aria-invalid={duplicateSceneNumber}
                                    className={`h-12 col-span-1 bg-secondary border-transparent focus-visible:ring-1 text-center mono ${
                                        duplicateSceneNumber
                                            ? "ring-1 ring-red-400 focus-visible:ring-red-400"
                                            : "focus-visible:ring-primary"
                                    }`}
                                />
                                <Input
                                    data-testid="scene-heading-input"
                                    placeholder="INT. HOUSE – DAY"
                                    value={sceneHeading}
                                    onChange={(e) => setSceneHeading(e.target.value)}
                                    className="h-12 col-span-2 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                />
                            </div>
                            {duplicateSceneNumber ? (
                                <p
                                    data-testid="scene-number-duplicate-error"
                                    className="text-xs text-red-400"
                                >
                                    Scene number already exists.
                                </p>
                            ) : null}
                            <div className="flex gap-2 flex-wrap">
                                {INT_EXT.map((v) => (
                                    <button
                                        type="button"
                                        key={v}
                                        data-testid={`scene-int-ext-${v.toLowerCase().replace("/", "-")}`}
                                        onClick={() => setSceneIntExt(sceneIntExt === v ? "" : v)}
                                        className={`tap min-tap px-3 rounded-full text-xs border transition-colors ${
                                            sceneIntExt === v
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-secondary text-neutral-300 border-white/10 hover:bg-white/10"
                                        }`}
                                    >
                                        {v}
                                    </button>
                                ))}
                                {TOD.map((v) => (
                                    <button
                                        type="button"
                                        key={v}
                                        data-testid={`scene-tod-${v.toLowerCase()}`}
                                        onClick={() => setSceneTod(sceneTod === v ? "" : v)}
                                        className={`tap min-tap px-3 rounded-full text-xs border transition-colors ${
                                            sceneTod === v
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-secondary text-neutral-300 border-white/10 hover:bg-white/10"
                                        }`}
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                            <Button
                                data-testid="add-scene-btn"
                                type="submit"
                                className="w-full min-tap"
                                disabled={!sceneHeading.trim() || duplicateSceneNumber}
                            >
                                <Plus size={16} className="mr-2" /> Add scene
                            </Button>
                        </form>

                        {confirmedScenes.length === 0 ? (
                            <EmptyScriptOnboarding
                                projectId={projectId}
                                variant="scenes"
                                role={project?.role}
                                canManageScript={project?.can_manage_script}
                            />
                        ) : (
                            <>
                                {/* iter-64 #2 — same sticky search shell
                                    as OnSetMode so Fittings and On Set
                                    feel like one system. Live search
                                    matches the same haystack (number,
                                    heading, INT/EXT, time-of-day,
                                    character names). */}
                                <div className="sticky top-14 z-40 py-2 bg-background/90 backdrop-blur-xl mb-5">
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-1">
                                            <Search
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                                            />
                                            <Input
                                                data-testid="prep-scene-search"
                                                value={sceneQuery}
                                                onChange={(e) => setSceneQuery(e.target.value)}
                                                placeholder="Search scenes"
                                                className="h-12 pl-11 text-base bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {filteredScenes.length === 0 ? (
                                    <p
                                        data-testid="prep-scene-search-empty"
                                        className="text-sm text-neutral-500 text-center py-6"
                                    >
                                        No scenes match &ldquo;{sceneQuery}&rdquo;.
                                    </p>
                                ) : (
                                    <ul data-testid="prep-scenes-list" className="space-y-2">
                                        {isTvMode
                                            ? // Iter-127 — Fittings episode accordion.
                                              // Iter-157P.17 — omitted scenes render
                                              // INLINE in scene-number order (a producer
                                              // looking for Scene 42 expects to find it
                                              // between 41 and 43). Visual distinction
                                              // for omission cause is preserved on the
                                              // row itself; the calm collapsed-section
                                              // approach from P.16 was reversed because
                                              // it broke production discoverability.
                                              groupedFilteredScenes.map((g) => {
                                                  if (g.scenes.length === 0) return null;
                                                  const activeCount = g.scenes.filter((s) => !s.omitted).length;
                                                  const omittedCount = g.scenes.length - activeCount;
                                                  const isCollapsed = collapsedEps.has(g.key);
                                                  const groupTestId =
                                                      g.key === NO_EPISODE
                                                          ? "prep-episode-none"
                                                          : `prep-episode-${g.key}`;
                                                  return (
                                                      <li key={g.key}>
                                                          <button
                                                              type="button"
                                                              data-testid={`${groupTestId}-toggle`}
                                                              onClick={() => toggleEpisode(g.key)}
                                                              aria-expanded={!isCollapsed}
                                                              className="tap w-full flex items-center justify-between gap-3 px-3 h-11 rounded-lg bg-secondary/60 border border-white/10 text-left hover:bg-white/10"
                                                          >
                                                              <span className="flex items-center gap-2">
                                                                  <ChevronDown
                                                                      size={14}
                                                                      className={`text-neutral-400 transition-transform ${
                                                                          isCollapsed ? "-rotate-90" : ""
                                                                      }`}
                                                                  />
                                                                  <span className="font-display text-sm font-bold text-white">
                                                                      {episodeLabelFor(g.key)}
                                                                  </span>
                                                              </span>
                                                              <span
                                                                  data-testid={`${groupTestId}-counts`}
                                                                  className="text-[10px] uppercase tracking-[0.2em] text-neutral-400"
                                                              >
                                                                  {activeCount} scene
                                                                  {activeCount === 1 ? "" : "s"}
                                                                  {omittedCount > 0 ? (
                                                                      <span className="ml-2 text-neutral-500">
                                                                          · {omittedCount} omitted
                                                                      </span>
                                                                  ) : null}
                                                              </span>
                                                          </button>
                                                          {!isCollapsed ? (
                                                              <ul
                                                                  data-testid={`${groupTestId}-list`}
                                                                  className="mt-2 space-y-2"
                                                              >
                                                                  {g.scenes.map((s) => renderPrepSceneRow(s, g.key))}
                                                              </ul>
                                                          ) : null}
                                                      </li>
                                                  );
                                              })
                                            : filteredScenes.map((s) => renderPrepSceneRow(s))}
                                    </ul>
                                )}
                            </>
                        )}
                    </section>
                ) : null}

                {tab === "characters" ? (
                    <section data-testid="characters-section">
                        <form
                            onSubmit={addCharacter}
                            className="rounded-xl border border-white/10 bg-card p-4 mb-5 space-y-3"
                        >
                            <Input
                                data-testid="character-name-input"
                                placeholder="Character name"
                                value={charName}
                                onChange={(e) => setCharName(e.target.value)}
                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                            <Input
                                data-testid="character-actor-input"
                                placeholder="Actor (optional)"
                                value={charActor}
                                onChange={(e) => setCharActor(e.target.value)}
                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                            <Button
                                data-testid="add-character-btn"
                                type="submit"
                                className="w-full min-tap"
                                disabled={!charName.trim()}
                            >
                                <Plus size={16} className="mr-2" /> Add character
                            </Button>
                        </form>

                        {characters.length === 0 ? (
                            <EmptyScriptOnboarding
                                projectId={projectId}
                                variant="cast"
                                role={project?.role}
                                canManageScript={project?.can_manage_script}
                            />
                        ) : (
                            <>
                                {/* Live cast search — same haystack as
                                    OnSetMode + AssignmentDialog (name,
                                    actor, character_number). */}
                                <div className="relative mb-3">
                                    <Search
                                        size={14}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                                    />
                                    <Input
                                        data-testid="prep-character-search"
                                        value={charQuery}
                                        onChange={(e) => setCharQuery(e.target.value)}
                                        placeholder="Search cast by name or number"
                                        className="h-12 pl-9 text-base bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                    />
                                </div>
                                {filteredCharacters.length === 0 ? (
                                    <p
                                        data-testid="prep-character-search-empty"
                                        className="text-sm text-neutral-500 text-center py-6"
                                    >
                                        No cast matches &ldquo;{charQuery}&rdquo;.
                                    </p>
                                ) : (
                                    <ul className="space-y-2" data-testid="characters-list">
                                        {filteredCharacters.map((c) => {
                                    const active = selectedChar?.id === c.id;
                                    return (
                                        <li key={c.id}>
                                            <button
                                                data-testid={`character-row-${c.id}`}
                                                onClick={() => {
                                                    // Iter-157J.3 — draft trust guard.
                                                    // Switching character would silently
                                                    // destroy any unsaved fitting entry
                                                    // for the current character. Detect
                                                    // and surface a Discard / Continue
                                                    // editing dialog before reassigning.
                                                    const next = active ? null : c;
                                                    if (
                                                        fittingStreamRef.current?.hasDraft?.() &&
                                                        next?.id !== selectedChar?.id
                                                    ) {
                                                        setDiscardConfirm({
                                                            reason: next ? "switch" : "close",
                                                            apply: () => setSelectedChar(next),
                                                        });
                                                        return;
                                                    }
                                                    setSelectedChar(next);
                                                }}
                                                className={`tap w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                                                    active
                                                        ? "border-primary bg-primary/10"
                                                        : "border-white/10 bg-card hover:bg-white/5"
                                                }`}
                                            >
                                                <div
                                                    data-testid={`character-row-avatar-${c.id}`}
                                                    className={`h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-white font-semibold ${
                                                        String(c.character_number || "").trim().length >= 3
                                                            ? "text-xs"
                                                            : "text-sm"
                                                    }`}
                                                >
                                                    {characterAvatar(c)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white text-sm font-medium truncate flex items-center gap-2">
                                                        <span className="truncate">{c.name}</span>
                                                        {/* Iter-158.13.2.20f.33 — Fittings Cast
                                                            picker allergy indicator. Inline in
                                                            the row's flex-1 col so it doesn't
                                                            get pushed past the Selected pill on
                                                            narrow widths. Wrapped in a passive
                                                            span (not the Link `AllergyBadge`
                                                            variant) — the whole row is a
                                                            button whose click selects the
                                                            character. Users get to the source
                                                            allergy note by tapping the badge
                                                            variant on the CharacterDetail /
                                                            ContinuityBlock surfaces. */}
                                                        {allergyByCharacterId.get(c.id) ? (
                                                            <span
                                                                data-testid={`prep-character-row-allergy-${c.id}`}
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-red-400/40 bg-red-500/15 text-red-200 text-[9px] uppercase tracking-[0.15em] font-semibold shrink-0"
                                                            >
                                                                <span aria-hidden="true">✚</span>
                                                                Allergy
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {c.actor ? (
                                                        <div className="text-xs text-neutral-500 truncate">
                                                            Played by {c.actor}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                {active ? (
                                                    <span className="text-[10px] uppercase tracking-[0.25em] text-primary">
                                                        Selected
                                                    </span>
                                                ) : null}
                                            </button>
                                        </li>
                                    );
                                })}
                                    </ul>
                                )}
                            </>
                        )}

                        {selectedChar ? (
                            <section
                                ref={selectedCharRef}
                                data-testid="prep-selected-character"
                                className="mt-5 rounded-xl border border-primary/40 bg-primary/5 p-5 scroll-mt-24"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            data-testid="prep-selected-character-avatar"
                                            className={`h-11 w-11 rounded-full bg-secondary text-white font-display font-bold flex items-center justify-center ${
                                                String(selectedChar.character_number || "").trim().length >= 3
                                                    ? "text-base"
                                                    : "text-lg"
                                            }`}
                                        >
                                            {characterAvatar(selectedChar)}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                                Selected
                                            </div>
                                            <div className="font-display text-lg font-bold text-white truncate flex items-center gap-2">
                                                <span className="truncate">{selectedChar.name}</span>
                                                {/* Iter-158.13.2.20f.33 — selected-character
                                                    header allergy indicator. Uses the Link
                                                    variant of AllergyBadge so a tap navigates
                                                    to the CharacterDetail allergy anchor. */}
                                                <AllergyBadge
                                                    note={allergyByCharacterId.get(selectedChar.id) || null}
                                                    to={`${routeBase}/${projectId}/characters/${selectedChar.id}#allergy`}
                                                    testid="prep-selected-character-allergy-badge"
                                                />
                                            </div>
                                            {selectedChar.actor ? (
                                                <div className="text-xs text-neutral-400 truncate">
                                                    Played by {selectedChar.actor}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {project?.role === "owner" ? (
                                            <button
                                                type="button"
                                                data-testid="prep-selected-character-edit"
                                                onClick={() => setEditCharacter(selectedChar)}
                                                aria-label="Edit cast member"
                                                title="Edit cast member"
                                                className="tap h-9 w-9 rounded-md text-neutral-300 inline-flex items-center justify-center"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                        ) : null}
                                        <button
                                            data-testid="prep-selected-character-close"
                                            onClick={() => {
                                                // Iter-157J.3 — draft trust guard
                                                // mirrors the character-row click
                                                // path. Closing the drawer would
                                                // discard any pending fitting
                                                // entry; we ask first.
                                                if (fittingStreamRef.current?.hasDraft?.()) {
                                                    setDiscardConfirm({
                                                        reason: "close",
                                                        apply: () => setSelectedChar(null),
                                                    });
                                                    return;
                                                }
                                                setSelectedChar(null);
                                            }}
                                            className="tap h-9 w-9 rounded-md text-neutral-400 inline-flex items-center justify-center"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div
                                    data-testid="fittings-unassigned-section"
                                    className="mt-6"
                                >
                                    <StateStream
                                        ref={fittingStreamRef}
                                        projectId={projectId}
                                        mode="prep"
                                        characterId={selectedChar.id}
                                        characterName={selectedChar.name}
                                        isOwner={project?.role === "owner"}
                                        viewOnly={project?.role === "viewer"}
                                        backHref={`${routeBase}/${projectId}/prep`}
                                        backLabel={`Back to ${selectedChar.name?.split(" ")[0] || "Fittings"}`}
                                    />
                                </div>
                                {/* Iter-158.13.2.20f.33b — Fittings Notes
                                    mounted on the ACTUAL H&M workflow.
                                    The initial .20f.33 build placed the
                                    Fittings Notes UI only on
                                    `pages/CharacterDetail.jsx`, but H&M
                                    users open a Character from Fittings
                                    → Cast which selects it in-place
                                    inside PrepMode's selected-character
                                    panel. Adding the reusable
                                    CharacterFittingsNotes here surfaces
                                    the + Note / + Allergy controls
                                    where the workflow actually lives.
                                    Same character-level notes surface,
                                    same allergy tag, same delete ACL
                                    — no data drift with the
                                    CharacterDetail mount. */}
                                <CharacterFittingsNotes
                                    projectId={projectId}
                                    characterId={selectedChar.id}
                                    canManageNotes={
                                        !project?.archived
                                        && (project?.role === "owner"
                                            || project?.role === "admin"
                                            || project?.role === "contributor")
                                    }
                                    canAdminNotes={
                                        !project?.archived
                                        && (project?.role === "owner"
                                            || project?.role === "admin")
                                    }
                                    currentUserId={project?.member?.user_id || null}
                                    testid="prep-character-fittings-notes"
                                />
                            </section>
                        ) : null}
                    </section>
                ) : null}

                {tab === "script" ? (
                    // Iter-158.7.1 — beta workspaces expose a single
                    // canonical script ingestion surface (Script
                    // Setup → episodic slots). Rendering PrepMode's
                    // paste/upload widgets under `/beta/hm-app/*`
                    // used to POST to the retired project-level
                    // endpoints (`/script/text`, `/script/upload`,
                    // `/script/parse`) which wrote scenes with
                    // `episode=null` alongside the correct
                    // Episode 1 tree, producing 114 rows from one
                    // walkthrough. The backend now serves HTTP 410
                    // Gone for those retired endpoints; here we hide
                    // the whole tab and point the producer at the
                    // canonical setup page.
                    apiBase.startsWith("/beta/") ? (
                        <section
                            data-testid="script-section-beta-retired"
                            className="space-y-4"
                        >
                            <div className="rounded-xl border border-white/10 bg-card p-6 space-y-3">
                                <p className="text-sm text-neutral-200">
                                    Script setup lives in one place for
                                    beta H&amp;M projects.
                                </p>
                                <p className="text-xs text-neutral-500">
                                    Upload, paste, parse and revision
                                    management have moved to Script
                                    Setup — one canonical entry point
                                    prevents duplicate scene trees.
                                </p>
                                <Button
                                    data-testid="script-section-beta-goto-setup"
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `${routeBase}/${projectId}/script`
                                        )
                                    }
                                    className="w-full min-tap font-semibold tracking-wide"
                                    variant="secondary"
                                >
                                    <FileText size={16} className="mr-2" />
                                    Open Script Setup
                                </Button>
                            </div>
                        </section>
                    ) : (
                    <section data-testid="script-section" className="space-y-4">
                        <div className="rounded-xl border border-white/10 bg-card p-4">
                            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                                Upload script file
                            </Label>
                            <p className="text-xs text-neutral-500 mt-1 mb-3">
                                PDF or DOCX (also TXT / Fountain). We&apos;ll extract text and keep the original.
                            </p>
                            <input
                                ref={fileRef}
                                type="file"
                                data-testid="script-file-input"
                                onChange={uploadScript}
                                accept=".pdf,.docx,.txt,.fountain"
                                className="hidden"
                            />
                            <Button
                                data-testid="trigger-script-upload"
                                type="button"
                                disabled={uploading}
                                onClick={() => fileRef.current?.click()}
                                className="w-full min-tap"
                                variant="secondary"
                            >
                                <Upload size={16} className="mr-2" />
                                {uploading
                                    ? "Uploading…"
                                    : script?.filename
                                    ? `Replace (${script.filename})`
                                    : "Choose file"}
                            </Button>
                            {script?.filename ? (
                                <div
                                    data-testid="script-uploaded-info"
                                    className="mt-3 text-xs text-neutral-500"
                                >
                                    Stored: <span className="text-neutral-300">{script.filename}</span>
                                </div>
                            ) : null}
                        </div>

                        <form
                            onSubmit={saveScriptText}
                            className="rounded-xl border border-white/10 bg-card p-4 space-y-3"
                        >
                            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                                Or paste script text
                            </Label>
                            <Input
                                data-testid="script-title-input"
                                placeholder="Title (optional)"
                                value={scriptTitle}
                                onChange={(e) => setScriptTitle(e.target.value)}
                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                            <Textarea
                                data-testid="script-text-input"
                                placeholder="Paste your script here…"
                                value={scriptText}
                                onChange={(e) => setScriptText(e.target.value)}
                                rows={10}
                                className="bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-y mono text-xs"
                            />
                            <Button
                                data-testid="save-script-text"
                                type="submit"
                                className="w-full min-tap"
                                variant="secondary"
                                disabled={!scriptText.trim()}
                            >
                                <FileText size={16} className="mr-2" /> Save pasted text
                            </Button>
                            {savedMsg ? (
                                <p data-testid="script-saved" className="text-xs text-emerald-400 text-center">
                                    {savedMsg}
                                </p>
                            ) : null}
                        </form>

                        <Button
                            data-testid="parse-script-btn"
                            type="button"
                            onClick={parseNow}
                            disabled={!scriptReady || parsing}
                            className="w-full min-tap font-semibold tracking-wide"
                        >
                            <Wand2 size={16} className="mr-2" />
                            {parsing ? "Building…" : "Build Scenes & Cast"}
                        </Button>
                        {!scriptReady ? (
                            <p className="text-xs text-neutral-500 text-center">
                                Upload or paste a script first.
                            </p>
                        ) : null}
                    </section>
                    )
                ) : null}
            </main>

            {/* iter-67 PART 2: Edit dialog for cast members from
                Fittings. Reuses the same component as CastHub so the
                edit logic / permissions stay identical. After save we
                update both the in-page list and the currently-selected
                character so the user stays exactly where they were. */}
            <EditCharacterDialog
                projectId={projectId}
                character={editCharacter}
                open={Boolean(editCharacter)}
                onOpenChange={(v) => {
                    if (!v) setEditCharacter(null);
                }}
                onSaved={(updated) => {
                    setCharacters((list) =>
                        list.map((c) => (c.id === updated.id ? updated : c)),
                    );
                    if (selectedChar?.id === updated.id) {
                        setSelectedChar(updated);
                    }
                    setEditCharacter(null);
                }}
            />

            {/* Iter-157J.3 — Discard / Continue editing dialog for
                unsaved fitting drafts. Triggered when an implicit-
                destroy action (switching character, closing the
                drawer, switching tab) would otherwise silently
                wipe out a pending fitting entry. On confirm we
                clear the StateStream draft via the ref then apply
                the deferred action. On cancel the action is
                abandoned and the user remains on the draft. */}
            <ConfirmDialog
                open={Boolean(discardConfirm)}
                onOpenChange={(v) => { if (!v) setDiscardConfirm(null); }}
                title="Unsaved fitting entry"
                description="You have an unfinished fitting entry. Discard it or keep editing?"
                confirmLabel="Discard draft"
                cancelLabel="Continue editing"
                tone="danger"
                onConfirm={() => {
                    const apply = discardConfirm?.apply;
                    fittingStreamRef.current?.discardDraft?.();
                    setDiscardConfirm(null);
                    if (typeof apply === "function") apply();
                }}
                testidPrefix="confirm-discard-fitting-draft"
            />
        </div>
    );
}
